<!--
 * @Author: caizexin caizexin@kjgb.net
 * @Date: 2026-08-11
 * @Description: 文件管理页面
-->
<template>
    <div>
        <!-- 文件列表 -->
        <div class="list_panel">
            <div class="filter_row">
                <span class="filter_label">{{ $t('code.app_name') }}：</span>
                <a-select
                    v-model:value="app_id"
                    :disabled="appIdDisabled"
                    :placeholder="$t('base.select')"
                    style="width: 200px;"
                    :options="appOptions"
                    @change="handleChangeAppId"
                    allowClear
                ></a-select>
                <span class="filter_label" style="margin-left: 16px;">{{ $t('file.category') }}：</span>
                <a-select
                    v-model:value="filterCategory"
                    :placeholder="$t('file.selectCategory')"
                    style="width: 180px;"
                    :options="categoryOptions"
                    @change="handleChangeCategory"
                    allowClear
                ></a-select>
            </div>
            <Table
                :table="table"
                @handleChangePagination="handleChangePagination"
            >
                <template v-slot:operateBtn>
                    <a-button type="primary" size="small" class="operate_btn" @click="handle('add')">
                        <component :is="$antIcons['PlusOutlined']"></component>
                        {{ $t('file.upload') }}
                    </a-button>
                </template>
                <template v-slot:tableAction="{props}">
                    <template v-if="props.column.key === 'action'">
                        <a-button type="link" size="small" style="margin-right:10px" @click="handle('download', props.record)">
                            {{ $t('base.download') }}
                        </a-button>
                        <a-button type="link" size="small" style="margin-right:10px" @click="handle('delete', props.record)">
                            {{ $t('base.delete') }}
                        </a-button>
                    </template>
                </template>
            </Table>
        </div>

        <!-- 上传文件弹窗 -->
        <a-modal
            v-model:visible="visible"
            :title="$t('file.upload')"
            width="600px"
            @cancel="handleCancel"
            @ok="handleOk"
        >
            <a-form layout="vertical" :model="formState">
                <a-form-item :label="$t('file.category')" v-bind="validateInfos.category">
                    <a-select
                        v-model:value="formState.category"
                        :placeholder="$t('file.selectCategory')"
                        style="width: 100%;"
                        :options="categoryOptions"
                    ></a-select>
                </a-form-item>
                <a-form-item :label="$t('code.app_name')" v-bind="validateInfos.appId">
                    <a-select
                        v-model:value="formState.appId"
                        :disabled="appIdDisabled"
                        :placeholder="$t('base.select')"
                        style="width: 100%;"
                        :options="appOptions"
                    ></a-select>
                </a-form-item>
                <a-form-item v-bind="validateInfos.file">
                    <a-upload
                        class="uploadFile"
                        v-model:file-list="fileList"
                        name="file"
                        action=""
                        :maxCount="1"
                        :before-upload="beforeUpload"
                        @change="handleFileChange"
                        @remove="handleFileRemove"
                    >
                        <a-button>
                            <upload-outlined></upload-outlined>
                            {{ $t('file.selectFile') }}
                        </a-button>
                    </a-upload>
                </a-form-item>
            </a-form>

            <template #footer>
                <a-button key="back" @click="handleCancel">{{$t('base.cancel')}}</a-button>
                <a-button key="submit" type="primary" :loading="uploading" @click="handleOk">{{$t('base.confirm')}}</a-button>
            </template>
        </a-modal>
    </div>
</template>

<script setup>
    import { ref, reactive, onMounted } from "vue";
    import Table from "@/components/Table.vue";
    import { useI18n } from 'vue-i18n';
    import { message, Modal } from 'ant-design-vue';
    import { UploadOutlined } from '@ant-design/icons-vue';
    import { FileApi } from '@/api/FileApi.js';
    import { SmallProgramApi } from '@/api/SmallProgramApi.js';
    import { Form } from 'ant-design-vue';

    const { t } = useI18n()

    const useForm = Form.useForm

    // ==================== 列表筛选 ====================
    const userInfo = ref(JSON.parse(localStorage.getItem('hd_ota_loginUser')))
    const app_id = ref(undefined)
    const appIdDisabled = ref(false)
    const filterCategory = ref(undefined)
    const appOptions = ref([])
    const categoryOptions = ref([])

    const visible = ref(false)

    const table = ref({
        data: [],
        columns: [
            {
                title: '#',
                dataIndex: 'serial',
                key: 'serial',
                width: 70,
                resizable: true,
                fixed: 'left',
            },
            {
                title: t('file.file_name'),
                dataIndex: 'file_name',
                key: 'file_name',
                width: 200,
                resizable: true,
            },
            {
                title: t('file.file_no'),
                dataIndex: 'file_no',
                key: 'file_no',
                width: 180,
                resizable: true,
            },
            // {
            //     title: t('file.file_size'),
            //     dataIndex: 'file_size',
            //     key: 'file_size',
            //     width: 120,
            //     resizable: true,
            // },
            {
                title: t('file.create_time'),
                dataIndex: 'create_time',
                key: 'create_time',
                width: 180,
                resizable: true,
            },
            {
                title: t('base.operate'),
                key: 'action',
                fixed: 'right',
                width: 120,
                resizable: true,
            },
        ],
        configs: {
            table: {
                pagination: {
                    total: 0,
                },
                rowClassName: (record, index) => (index % 2 === 1 ? 'table-striped' : null)
            },
            scroll: {
                x: 1200,
                y: 450,
            },
            rowSelection: false,
            loading: false,
            pagination: {
                hideOnSinglePage: false,
                position: ['bottomLeft'],
                responsive: true,
                current: 1,
                pageSize: 10,
                pageSizeOptions: ['10', '20', '50'],
                total: 0,
                showTotal: () => t('base.total') + ` ${table.value.configs.pagination.total} ` + t('base.row')
            },
            operateBtn: {
                showSearch: false,
                showAdd: false,
            }
        },
    })

    // ==================== 上传弹窗 ====================

    const formState = reactive({
        category: undefined,
        appId: undefined,
        file: null,
    })
    const rulesRef = reactive({
        category: [
            { required: true, message: '请选择文件分类！' },
        ],
        appId: [
            { required: true, message: '请选择小程序！' },
        ],
        file: [
            {
                validator: (_rule, value) => {
                    if (!value) {
                        return Promise.reject('请选择文件！')
                    }
                    return Promise.resolve()
                }
            },
        ],
    })

    const fileList = ref([])
    const uploading = ref(false)

    const { validate, validateInfos, resetFields } = useForm(formState, rulesRef, {})

    const beforeUpload = (file) => {
        if (file.size / 1024 / 1024 > 100) {
            message.error('文件大小不能超过100M！')
            return false
        }
        formState.file = file
        return false
    }

    const handleFileChange = (val) => {
        if (val.file && val.file.originFileObj) {
            formState.file = val.file.originFileObj
        }
    }

    const handleFileRemove = () => {
        formState.file = null
    }

    // ==================== 操作分发 ====================

    const handle = async (type, row) => {
        switch (type) {
            case 'add':
                visible.value = true
                fileList.value = []
                formState.category = categoryOptions.value.length > 0 ? categoryOptions.value[0].value : undefined
                formState.file = null
                resetFields()
                if(userInfo.value.role_type == 2){
                    formState.appId = userInfo.value.app_id
                    console.log('formState.appId',formState.appId)
                } else {
                    formState.appId = app_id.value
                }
                break
            case 'download':
                // let blob = await FileApi.getInstance().fileDownload(row.file_no)
                // let downloadUrl = URL.createObjectURL(blob)
                // let a = document.createElement('a')
                // a.href = downloadUrl
                // a.download = row.file_name
                // a.style.display = 'none'
                // document.body.appendChild(a)
                // a.click()
                // document.body.removeChild(a)
                // URL.revokeObjectURL(downloadUrl)

                let res = await FileApi.getInstance().fileDownload(row.file_no)
                if (res.code == 0) {
                    window.open(res.data)
                    message.success(t('base.downloadSuccess'))
                } else {
                    message.error(res.msg)
                }
                break
            case 'delete':
                Modal.confirm({
                    title: '提示',
                    content: '是否确认删除？ 删除后将无法恢复',
                    okText: t('base.yes'),
                    cancelText: t('base.no'),
                    onOk() {
                        handleDelete(row)
                    }
                })
                
        }
    }

    const handleOk = () => {
        validate().then(async () => {
            uploading.value = true
            let formData = new FormData()
            formData.append('file', formState.file)
            formData.append('cateCode', formState.category)
            formData.append('appId', formState.appId)

            let res = await FileApi.getInstance().uploadFile(formData)
            uploading.value = false
            if (res.code == 0) {
                message.success(t('file.uploadSuccess'))
                visible.value = false
                fileList.value = []
                resetFields()
                table.value.configs.pagination.current = 1
                getList()
            } else {
                message.error(res.msg)
            }
        }).catch(err => {
            uploading.value = false
            console.log('error', err)
        })
    }
    const handleDelete = async (row) => {
        let res = await FileApi.getInstance().fileDelete(row.file_no)
        if (res.code === 0) {
            message.success(res.msg)
            getList()
        } else {
            message.error(res.msg)
        }
    }

    const handleCancel = () => {
        resetFields()
        fileList.value = []
        formState.file = null
    }

    // ==================== 列表相关 ====================

    const handleChangeAppId = (value) => {
        app_id.value = value
        table.value.configs.pagination.current = 1
        getList()
    }

    const handleChangeCategory = (value) => {
        filterCategory.value = value
        table.value.configs.pagination.current = 1
        getList()
    }

    const handleChangePagination = (config, _data) => {
        table.value.configs.pagination.current = config.current
        table.value.configs.pagination.pageSize = config.pageSize
        getList()
    }

    const getList = async () => {
        table.value.configs.loading = true

        let param = {}
        if (app_id.value) param.appId = app_id.value
        if (filterCategory.value) param.cateCode = filterCategory.value
        param.page_no = table.value.configs.pagination.current
        param.page_size = table.value.configs.pagination.pageSize

        try {
            let res = await FileApi.getInstance().getFileList(param)
            if (res.code == 0) {
                let data = res.data
                data.records.forEach((i, index) => {
                    i.serial = (table.value.configs.pagination.current - 1) * table.value.configs.pagination.pageSize + index + 1
                })
                table.value.data = data.records
                table.value.configs.pagination.total = data.total
                table.value.configs.loading = false
            } else {
                message.error(res.msg)
                table.value.configs.loading = false
            }
        } catch (err) {
            table.value.configs.loading = false
            message.error('获取文件列表失败')
        }
    }

    // ==================== 下拉选项初始化 ====================

    const getAppNameList = async () => {
        let param = { status: 0 }
        let res = await SmallProgramApi.getInstance().getSmallProgramQuery(param)
        if (res.code == 0 && res.data.length > 0) {
            res.data.forEach(item => {
                appOptions.value.push({
                    label: item.app_name,
                    value: item.app_id,
                })
            })
            if(userInfo.value.role_type == 2){
                app_id.value = userInfo.value.app_id
                appIdDisabled.value = true
                return;
            }
            appIdDisabled.value = false
            app_id.value = appOptions.value[0].value
        } else {
            message.error(res.msg)
        }
    }

    const getCategoryList = async () => {
        let res = await FileApi.getInstance().getCategoryList()
        if (res.code == 0 && res.data.length > 0) {
            res.data.forEach(item => {
                categoryOptions.value.push({
                    label: item.cate_value,
                    value: item.cate_code,
                })
            })
            filterCategory.value = categoryOptions.value[0].value
        } else {
            message.error(res.msg)
        }
    }

    onMounted(async () => {
        await getAppNameList()
        await getCategoryList()
        getList()
    })
</script>

<style lang="scss" scoped>
.list_panel {
    .filter_row {
        margin-bottom: 10px;
        display: flex;
        align-items: center;

        .filter_label {
            color: #333;
            font-size: 14px;
            white-space: nowrap;
        }
    }
}

.uploadFile {
    ::v-deep .ant-upload-list-item-name,
    ::v-deep .anticon-paper-clip {
        color: #1890ff;
    }
}

::v-deep .ant-table tfoot>tr>td,
::v-deep .ant-table tfoot>tr>th,
::v-deep .ant-table-tbody>tr>td,
::v-deep .ant-table-thead>tr>th {
    overflow-wrap: break-word;
    padding: 14px;
    position: relative;
}
</style>
